import { Form, Formik } from "formik";
import { Building2, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { getFirm, type FirmProfile, updateFirm } from "@/features/settings/firm-api";
import { BrandingSection } from "@/features/settings/components/tabs/branding-section";
import { FIELD_LABEL, SettingsSection } from "@/features/settings/components/settings-section";
import { ApiError } from "@/lib/api";

const companySchema = Yup.object({
  name: Yup.string().trim().required("Company name is required").max(120),
  email: Yup.string().email("Enter a valid email").max(254),
  phone: Yup.string().max(32),
  addressLine1: Yup.string().max(120),
  addressLine2: Yup.string().max(120),
  city: Yup.string().max(60),
  state: Yup.string().max(2, "Use the 2-letter code"),
  zipCode: Yup.string().max(10),
  billDiff: Yup.boolean(),
  billAddressLine1: Yup.string().max(120),
  billAddressLine2: Yup.string().max(120),
  billCity: Yup.string().max(60),
  billState: Yup.string().max(2, "Use the 2-letter code"),
  billZipCode: Yup.string().max(10),
});

export function CompanyTab() {
  const [firm, setFirm] = useState<FirmProfile | null>(null);
  const [error, setError] = useState(false);
  // Website has no backend field yet — kept as local UI state so it matches the
  // design. TODO(api): add `website` to the firm model/serializer and wire this.
  const [website, setWebsite] = useState("");
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");

  useEffect(() => {
    let active = true;
    getFirm()
      .then((f) => active && setFirm(f))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(values: Omit<FirmProfile, "id">) {
    try {
      const updated = await updateFirm(values);
      setFirm(updated);
      toast.success("Company details saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  const loading = !firm && !error;

  return (
    <div className="flex flex-col gap-6">
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load this section. Please refresh and try again.
        </p>
      ) : (
        firm && (
          <>
            <SetupProgress firm={firm} />
            <Formik
              initialValues={{
                name: firm.name,
                email: firm.email,
                phone: firm.phone,
                addressLine1: firm.addressLine1,
                addressLine2: firm.addressLine2,
                city: firm.city,
                state: firm.state,
                zipCode: firm.zipCode,
                billDiff: firm.billDiff,
                billAddressLine1: firm.billAddressLine1,
                billAddressLine2: firm.billAddressLine2,
                billCity: firm.billCity,
                billState: firm.billState,
                billZipCode: firm.billZipCode,
              }}
              validationSchema={companySchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, dirty, values, resetForm }) => {
                const fieldsToFill = [
                  values.email,
                  values.phone,
                  website,
                  values.addressLine1,
                  values.city,
                  values.state,
                  values.zipCode,
                ].filter((v) => !v?.trim()).length;
                return (
                  <Form className="flex flex-col gap-6">
                    {!isAdmin && (
                      <p className="text-muted-foreground text-sm">
                        Only a country director can edit these details.
                      </p>
                    )}
                    <fieldset disabled={!isAdmin} className="m-0 flex flex-col gap-6 border-0 p-0">
                      <SettingsSection
                        title="Company"
                        icon={Building2}
                        iconWellClassName="bg-accent text-accent-foreground"
                        subtitle="Your legal name and where the IRS and clients can reach you."
                        headerAction={
                          fieldsToFill > 0 ? (
                            <span className="bg-warning-soft text-warning inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
                              <span className="bg-primary size-1.5 rounded-full" />
                              {fieldsToFill} {fieldsToFill === 1 ? "field" : "fields"} to fill
                            </span>
                          ) : undefined
                        }
                        bodyClassName="block p-0"
                      >
                        <div className="grid grid-cols-1 gap-x-[18px] gap-y-4 p-6 md:grid-cols-3">
                          <div className="max-w-[480px] md:col-span-3">
                            <FormField
                              name="name"
                              label="Company Name"
                              labelClassName={FIELD_LABEL}
                            />
                          </div>
                          <FormField
                            name="email"
                            label="Email"
                            type="email"
                            placeholder="hello@yourfirm.com"
                            labelClassName={FIELD_LABEL}
                          />
                          <FormField
                            name="phone"
                            label="Phone"
                            placeholder="(555) 000-0000"
                            labelClassName={FIELD_LABEL}
                          />
                          <div className="grid gap-2">
                            <Label htmlFor="website" className={FIELD_LABEL}>
                              Website{" "}
                              <span className="text-muted-foreground/70 font-medium tracking-normal normal-case">
                                optional
                              </span>
                            </Label>
                            <Input
                              id="website"
                              type="url"
                              placeholder="https://"
                              value={website}
                              onChange={(e) => setWebsite(e.target.value)}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <FormField
                              name="addressLine1"
                              label="Address"
                              placeholder="Street address"
                              labelClassName={FIELD_LABEL}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <FormField
                              name="addressLine2"
                              label="Address line 2"
                              placeholder="Suite, floor, building (optional)"
                              labelClassName="sr-only"
                            />
                          </div>
                          <FormField
                            name="city"
                            label="City"
                            placeholder="City"
                            labelClassName={FIELD_LABEL}
                          />
                          <FormField
                            name="state"
                            label="State"
                            maxLength={2}
                            placeholder="CA"
                            className="uppercase"
                            labelClassName={FIELD_LABEL}
                          />
                          <FormField
                            name="zipCode"
                            label="ZIP Code"
                            placeholder="00000"
                            labelClassName={FIELD_LABEL}
                          />
                        </div>
                        {/* Billing (remit-to) address — inline footer; billing
                            fields appear only when flagged different. */}
                        <div className="bg-muted/40 border-t px-6 py-5">
                          <CheckboxField
                            name="billDiff"
                            label="Bill my subscription to a different address"
                            hint="Where your firm's own invoices and subscription are billed. Off = same as above."
                          />
                          {values.billDiff && (
                            <div className="mt-6 grid grid-cols-1 gap-x-[18px] gap-y-4 md:grid-cols-3">
                              <div className="md:col-span-3">
                                <FormField
                                  name="billAddressLine1"
                                  label="Address"
                                  placeholder="Street address"
                                  labelClassName={FIELD_LABEL}
                                />
                              </div>
                              <div className="md:col-span-3">
                                <FormField
                                  name="billAddressLine2"
                                  label="Address line 2"
                                  placeholder="Suite, floor, building (optional)"
                                  labelClassName="sr-only"
                                />
                              </div>
                              <FormField
                                name="billCity"
                                label="City"
                                placeholder="City"
                                labelClassName={FIELD_LABEL}
                              />
                              <FormField
                                name="billState"
                                label="State"
                                maxLength={2}
                                placeholder="CA"
                                className="uppercase"
                                labelClassName={FIELD_LABEL}
                              />
                              <FormField
                                name="billZipCode"
                                label="ZIP Code"
                                placeholder="00000"
                                labelClassName={FIELD_LABEL}
                              />
                            </div>
                          )}
                        </div>
                      </SettingsSection>

                      <BrandingSection isAdmin={isAdmin} />
                    </fieldset>

                    {isAdmin && (
                      <div className="bg-card shadow-card sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-5 py-3.5">
                        <span className="text-muted-foreground inline-flex items-center gap-2.5 text-[13px]">
                          <span
                            className={cn(
                              "size-2 rounded-full",
                              dirty ? "bg-primary" : "bg-success",
                            )}
                          />
                          <strong className="text-foreground font-semibold">
                            {dirty ? "Unsaved changes" : "All changes saved"}
                          </strong>
                          {dirty && (
                            <span className="hidden sm:inline">· company details and branding</span>
                          )}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!dirty || isSubmitting}
                            onClick={() => resetForm()}
                          >
                            Discard
                          </Button>
                          <Button type="submit" variant="cta" disabled={!dirty || isSubmitting}>
                            <Check /> {isSubmitting ? "Saving…" : "Save changes"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </>
        )
      )}
    </div>
  );
}

/** Navy setup-progress hero (mockup): how far the firm is through first setup. */
function SetupProgress({ firm }: { firm: FirmProfile }) {
  // Name + address derive from real fields; logo/signatures/portal need their own
  // status endpoints. TODO(api): wire logo/signatures/portal completion flags.
  const steps = [
    { label: "Name", done: !!firm.name },
    { label: "Logo", done: false },
    { label: "Address & contact", done: !!(firm.addressLine1 && firm.city && firm.email) },
    { label: "Signatures", done: false },
    { label: "Client portal", done: false },
  ];
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / steps.length) * 100);
  const firstPending = steps.findIndex((s) => !s.done);
  return (
    <Card className="bg-brand-panel relative gap-4 overflow-hidden border-0 px-7 py-6 text-white">
      <span
        aria-hidden
        className="bg-primary/25 pointer-events-none absolute -top-24 -right-24 size-72 rounded-full blur-3xl"
      />
      <div className="relative flex flex-col gap-1.5">
        <span className="text-brand-panel-muted text-xs font-semibold tracking-[0.08em] uppercase">
          Firm identity
        </span>
        <h2 className="font-heading text-2xl font-semibold tracking-tight">
          {firm.name || "Your firm"} is <span className="text-primary">{pct}%</span> set up.
        </h2>
        <p className="text-brand-panel-muted max-w-2xl text-[13px]">
          Everything here flows onto your letters, invoices and the client portal — finish it once
          and every document looks like yours.
        </p>
      </div>
      <ol className="relative m-0 flex list-none flex-wrap gap-2 p-0">
        {steps.map((s, i) => (
          <li
            key={s.label}
            className={cn(
              "inline-flex h-9 items-center gap-2 rounded-[10px] px-3 text-[13px]",
              s.done
                ? "bg-white/15 font-semibold text-white"
                : "text-brand-panel-muted border border-dashed border-white/35 font-medium",
            )}
          >
            {s.done ? (
              <Check className="size-3.5 text-emerald-400" />
            ) : (
              <span
                className={cn(
                  "size-2 rounded-full",
                  i === firstPending ? "bg-primary" : "bg-white/35",
                )}
              />
            )}
            {s.label}
          </li>
        ))}
      </ol>
    </Card>
  );
}
